import { ref } from 'vue'
import { signOut, onAuthStateChanged } from 'firebase/auth'
import { doc, setDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import { useRouter } from 'vue-router'
import { devLog } from '@/utils/environment'
import { useAuthStore } from '@/store/authStore'
import { canWriteDistrictSettings } from '@/utils/schoolSettings'
import {
  activeSchoolId,
  listenEffectiveSecurity,
  writeSecurityFields
} from '@/composables/listenEffectiveSecurity'
import { auditLogger } from '@/utils/auditLogger'
import { loginHrefForCurrentLocation } from '@/utils/schoolRoutes'

class SessionTimeoutManager {
  constructor() {
    this.timeoutId = null
    this.warningTimeoutId = null
    this.isEnabled = ref(false)
    this.timeoutMinutes = ref(30)
    this.showWarning = ref(false)
    this.warningCountdown = ref(0)
    this.lastActivity = Date.now()
    this.lastLogTime = null
    this.router = null
    this.unsubscribeSettings = null

    // Activity events to monitor (removed mousemove for performance)
    this.activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click', 'keydown']

    this.init()
  }

  init() {
    devLog('🔧 Session timeout system initialization STARTED')

    // Set up activity listeners
    this.setupActivityListeners()

    // Watch for auth state changes. The Firestore settings listener must NOT
    // start before sign-in: an unauthenticated onSnapshot is permission-denied
    // and a denied listener dies permanently — settings would never load.
    this.setupAuthWatcher()
  }

  setupAuthWatcher() {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        devLog('🔧 User logged in, starting session timeout system')
        // (Re)subscribe to settings now that rules allow the read.
        this.loadSettings()
        // Start timeout when user logs in - longer delay to ensure settings are loaded
        setTimeout(() => {
          this.resetTimeout()
        }, 2000) // Increased delay to ensure settings listener is established
      } else {
        devLog('🔧 User logged out, clearing session timeouts')
        // Clear timeouts and drop the settings listener when user logs out
        if (this.unsubscribeSettings) {
          this.unsubscribeSettings()
          this.unsubscribeSettings = null
        }
        this.clearTimeouts()
        this.hideWarning()
      }
    })
  }

  async loadSettings() {
    try {
      // Replace any previous listener (e.g. re-login in the same tab).
      if (this.unsubscribeSettings) {
        this.unsubscribeSettings()
        this.unsubscribeSettings = null
      }
      this.unsubscribeSettings = listenEffectiveSecurity({
        onEffective: (data) => {
          // Explicit false turns it off. A missing district doc stays on.
          const newEnabled = data?.sessionTimeoutEnabled !== false
          const newMinutes = data?.sessionTimeoutMinutes || 30
          devLog(
            `🔧 Session timeout settings updated: enabled=${newEnabled}, timeout=${newMinutes}min`
          )
          this.isEnabled.value = newEnabled
          this.timeoutMinutes.value = newMinutes
          if (auth.currentUser) this.resetTimeout()
        },
        onDistrictMissing: () => {
          devLog('🔧 No session timeout settings found, creating with defaults')
          let role = null
          try {
            role = useAuthStore().currentUser?.role || null
          } catch {
            /* Pinia inactive */
          }
          if (!canWriteDistrictSettings(role)) return
          setDoc(
            doc(db, 'app_settings', 'security'),
            {
              sessionTimeoutEnabled: true,
              sessionTimeoutMinutes: 30,
              createdAt: new Date().toISOString(),
              createdBy: 'system_auto_create'
            },
            { merge: true }
          ).catch((error) => {
            console.error('Failed to create default session timeout settings:', error)
          })
        },
        onError: () => {
          this.isEnabled.value = true
          this.timeoutMinutes.value = 30
        }
      })
    } catch (error) {
      console.error('Failed to setup session timeout settings listener:', error)
      // Use defaults on error
      this.isEnabled.value = true
      this.timeoutMinutes.value = 30
    }
  }

  async updateSettings(enabled, minutes) {
    try {
      await writeSecurityFields(useAuthStore().currentUser, activeSchoolId(), {
        sessionTimeoutEnabled: enabled,
        sessionTimeoutMinutes: minutes
      })

      // Immediately update local values and restart timeout
      this.isEnabled.value = enabled
      this.timeoutMinutes.value = minutes

      devLog(`✅ Session timeout settings updated: enabled=${enabled}, timeout=${minutes}min`)

      // Restart timeout with new settings if user is logged in
      if (auth.currentUser) {
        this.resetTimeout()
      }
    } catch (error) {
      console.error('Failed to update session timeout settings:', error)
      throw error
    }
  }

  setupActivityListeners() {
    // Create bound handler to ensure proper cleanup
    this.boundActivityHandler = this.handleActivity.bind(this)
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.boundActivityHandler, true)
    })
  }

  handleActivity() {
    if (!this.isEnabled.value || !auth.currentUser) return

    const now = Date.now()

    // Reduced throttling - only every 10 seconds for more responsive timeout resets
    if (now - this.lastActivity < 10000) {
      this.lastActivity = now
      return
    }

    this.lastActivity = now

    // Always reset timeout on activity
    devLog('🔄 Activity detected - resetting session timeout')
    this.resetTimeout()

    // Hide warning if showing
    if (this.showWarning.value) {
      this.hideWarning()
    }
  }

  resetTimeout() {
    if (!this.isEnabled.value || !auth.currentUser) {
      devLog('⚠️ Session timeout disabled or no user - skipping reset')
      return
    }

    // Clear existing timeouts
    this.clearTimeouts()

    const timeoutMs = Math.round(this.timeoutMinutes.value * 60 * 1000)
    const warningMs = Math.max(5000, timeoutMs - 2 * 60 * 1000) // Show warning 2 minutes before timeout

    devLog(
      `🔒 Session timeout reset: ${this.timeoutMinutes.value} minutes (${timeoutMs}ms), warning in ${warningMs}ms`
    )

    // Set warning timeout
    this.warningTimeoutId = setTimeout(() => {
      devLog('🚨 Showing session warning')
      this.showSessionWarning()
    }, warningMs)

    // Set logout timeout
    this.timeoutId = setTimeout(() => {
      devLog('⏰ Session timeout triggered - logging out user')
      this.handleSessionTimeout()
    }, timeoutMs)
  }

  showSessionWarning() {
    if (this.showWarning.value) return // Prevent multiple warnings

    this.showWarning.value = true
    this.warningCountdown.value = Math.min(120, Math.floor((this.timeoutMinutes.value * 60) / 2)) // 2 minutes or half the timeout, whichever is smaller

    devLog(`🚨 Session warning displayed, countdown: ${this.warningCountdown.value} seconds`)

    // Start countdown
    const countdownInterval = setInterval(() => {
      this.warningCountdown.value--

      if (this.warningCountdown.value <= 0) {
        clearInterval(countdownInterval)
        this.handleSessionTimeout()
      }
    }, 1000)

    // Store interval ID for cleanup
    this.countdownInterval = countdownInterval
  }

  hideWarning() {
    this.showWarning.value = false
    this.warningCountdown.value = 0

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  async handleSessionTimeout() {
    devLog('🔒 Handling session timeout')

    try {
      // Delivery is bounded and reports failures without preventing sign-out.
      if (auth.currentUser) {
        await auditLogger.logSystemAccess('session_timeout', {
          timeoutMinutes: this.timeoutMinutes.value,
          lastActivity: new Date(this.lastActivity).toISOString()
        })
      }

      // Clear timeouts and hide warning
      this.clearTimeouts()
      this.hideWarning()

      // Sign out user
      devLog('🚨 SESSION TIMEOUT LOGOUT: About to call signOut()')
      devLog('🔍 Session timeout stack trace:', new Error().stack)
      await signOut(auth)

      devLog('🚪 User signed out due to session timeout')

      // Keep the page they were on (e.g. a feedback form) so sign-in returns there.
      const loginHref = loginHrefForCurrentLocation()
      if (this.router) {
        this.router.push(loginHref)
      } else {
        window.location.assign(loginHref)
      }
    } catch (error) {
      console.error('Error during session timeout:', error)
      // Force redirect even if there's an error
      window.location.assign(loginHrefForCurrentLocation())
    }
  }

  extendSession() {
    this.hideWarning()
    this.resetTimeout()
  }

  clearTimeouts() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId)
      this.warningTimeoutId = null
    }
  }

  setRouter(router) {
    this.router = router
  }

  destroy() {
    this.clearTimeouts()
    this.hideWarning()

    // Remove activity listeners using the bound handler
    if (this.boundActivityHandler) {
      this.activityEvents.forEach((event) => {
        document.removeEventListener(event, this.boundActivityHandler, true)
      })
    }

    // Unsubscribe from settings listener
    if (this.unsubscribeSettings) {
      this.unsubscribeSettings()
      this.unsubscribeSettings = null
    }
  }
}

// Create singleton instance
const sessionTimeoutManager = new SessionTimeoutManager()

export function useSessionTimeout() {
  const router = useRouter()
  sessionTimeoutManager.setRouter(router)

  return {
    isEnabled: sessionTimeoutManager.isEnabled,
    timeoutMinutes: sessionTimeoutManager.timeoutMinutes,
    showWarning: sessionTimeoutManager.showWarning,
    warningCountdown: sessionTimeoutManager.warningCountdown,
    updateSettings: sessionTimeoutManager.updateSettings.bind(sessionTimeoutManager),
    extendSession: sessionTimeoutManager.extendSession.bind(sessionTimeoutManager),
    resetTimeout: sessionTimeoutManager.resetTimeout.bind(sessionTimeoutManager)
  }
}
